import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Extend window interface for audio context
declare global {
  interface Window {
    audioContext?: AudioContext;
  }
}

interface UseVoiceConversationReturn {
  startVoiceConversation: () => Promise<void>;
  endVoiceConversation: () => void;
  isConnected: boolean;
  isConnecting: boolean;
  isAgentSpeaking: boolean;
  conversationId?: string;
  canRetry: boolean;
  isProcessing: boolean;
}

export const useVoiceConversation = (positId: string): UseVoiceConversationReturn => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  
  // Smart buffering state for transcript accumulation
  const [transcriptBuffer, setTranscriptBuffer] = useState<string[]>([]);
  const [lastTranscriptTime, setLastTranscriptTime] = useState<number>(0);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const isStartingRef = useRef(false);
  
  // TTS function using ElevenLabs HTTP API (simpler and more reliable)
  const playTextToSpeech = useCallback(async (text: string) => {
    try {
      setIsAgentSpeaking(true);
      
      // Get API key from our edge function
      const { data: apiData, error: apiError } = await supabase.functions.invoke('elevenlabs-voice-webhook', {
        body: {
          action: 'get_api_key'
        }
      });
      
      if (apiError || !apiData?.api_key) {
        setIsAgentSpeaking(false);
        return;
      }
      
      // Use ElevenLabs HTTP API for TTS
      const voiceId = 'UgBBYS2sOqTuMpoF3BR0'; // User's preferred voice
      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiData.api_key
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_monolingual_v1',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.8
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`TTS API error: ${response.status}`);
      }
      
      // Convert response to audio blob and play
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      // Store reference for interruption capability
      currentAudioRef.current = audio;
      
      audio.play()
        .then(() => {
          audio.onended = () => {
            URL.revokeObjectURL(audioUrl);
            setIsAgentSpeaking(false);
            currentAudioRef.current = null; // Clear reference when done
          };
        })
        .catch(error => {
          URL.revokeObjectURL(audioUrl);
          setIsAgentSpeaking(false);
          currentAudioRef.current = null; // Clear reference on error
        });
      
    } catch (error) {
      setIsAgentSpeaking(false);
    }
  }, []);
  
  const processAccumulatedTranscripts = useCallback(async (combinedMessage: string) => {
    // Stop any currently playing TTS audio (interruption)
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsAgentSpeaking(false);
    }
    
    // Clear transcript buffer since we're processing
    setTranscriptBuffer([]);
    
    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('chat-with-posit-v4-unified', {
        body: {
          query: combinedMessage,
          posit_id: positId,
          user_id: user?.id,
          session_id: null, // Use null to avoid FK constraint issues with non-existent session IDs
          voiceMode: true,
          voice_call: true
        }
      });

      if (error) {
        return;
      }
      
      // Send response to TTS WebSocket for audio generation
      if (data.content) {
        await playTextToSpeech(data.content);
      }

    } catch (error) {
    } finally {
      setIsProcessing(false);
    }
  }, [positId, user?.id, conversationId]);

  // Smart transcript buffering with silence detection
  const handleTranscriptReceived = useCallback((transcript: string) => {
    // Don't buffer if already processing
    if (isProcessing) {
      return;
    }
    
    // Add to buffer and update timestamp
    setTranscriptBuffer(prev => [...prev, transcript]);
    setLastTranscriptTime(Date.now());
    
    // Clear existing timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    
    // Set new timeout for silence detection (2 seconds)
    silenceTimeoutRef.current = setTimeout(() => {
      setTranscriptBuffer(currentBuffer => {
        if (currentBuffer.length > 0) {
          const combinedTranscript = currentBuffer.join(' ').trim();
          processAccumulatedTranscripts(combinedTranscript);
          return []; // Clear buffer
        }
        return currentBuffer;
      });
    }, 2000); // 2 second silence threshold
  }, [isProcessing, processAccumulatedTranscripts]);

  // Cleanup function for silence timeout
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  const startAudioCapture = useCallback(async () => {
    try {
      // Check if running in Capacitor (mobile app)
      const isCapacitor = !!(window as any).Capacitor;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          // Capacitor-specific constraints
          ...(isCapacitor && {
            autoGainControl: true,
            latency: 0.01
          })
        }
      });
      
      // Create AudioContext for processing
      const audioContext = new AudioContext({ sampleRate: 16000 });
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = (event) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const inputData = event.inputBuffer.getChannelData(0);
          
          // Convert float32 to int16 PCM
          const pcmData = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            pcmData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }
          
          // Convert to base64
          const uint8Array = new Uint8Array(pcmData.buffer);
          const base64Audio = btoa(String.fromCharCode(...uint8Array));
          
          wsRef.current.send(JSON.stringify({
            user_audio_chunk: base64Audio
          }));
        }
      };
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Store references for cleanup
      mediaRecorderRef.current = { 
        stream, 
        audioContext, 
        processor,
        stop: () => {
          processor.disconnect();
          source.disconnect();
          audioContext.close();
          stream.getTracks().forEach(track => track.stop());
        }
      } as any;

    } catch (error) {
    }
  }, []);

  const stopAudioCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  }, []);

  const startVoiceConversation = useCallback(async () => {
    if (isStartingRef.current || isConnected) {
      return;
    }

    // Wait for user to be available before starting
    if (!user?.id) {
      setTimeout(() => startVoiceConversation(), 1000);
      return;
    }

    isStartingRef.current = true;
    setIsConnecting(true);
    setCanRetry(false);

    try {
      // Get signed URL from our edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke('elevenlabs-voice-webhook', {
        body: {
          action: 'get_signed_url',
          user_id: user?.id,
          posit_id: positId
        }
      });

      if (urlError || !urlData?.signed_url) {
        throw new Error('Failed to get signed URL: ' + (urlError?.message || 'No URL returned'));
      }

      // Create WebSocket connection
      const ws = new WebSocket(urlData.signed_url);
      wsRef.current = ws;

      ws.onopen = async () => {
        setIsConnected(true);
        setIsConnecting(false);
        setCanRetry(true);
        retryCountRef.current = 0;
        isStartingRef.current = false;

        const sessionId = `voice-${Date.now()}-${user?.id}`;
        setConversationId(sessionId);

        // Initialize conversation
        ws.send(JSON.stringify({
          type: "conversation_initiation_client_data",
          conversation_config_override: {
            agent: {
              language: "en"
            }
          }
        }));

        // Start capturing and sending audio
        await startAudioCapture();
      };

              ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

          // Handle ping events
          if (data.type === 'ping') {
            ws.send(JSON.stringify({
              type: 'pong',
              event_id: data.ping_event.event_id
            }));
            return;
          }

          // Handle user transcript
          if (data.type === 'user_transcript') {
            const transcript = data.user_transcription_event?.user_transcript;
            if (transcript) {
              handleTranscriptReceived(transcript);
            }
          }

          // Handle agent responses
          if (data.type === 'agent_response') {
            setIsAgentSpeaking(true);
            setTimeout(() => setIsAgentSpeaking(false), 2000);
          }

          // Handle audio responses - manually play the audio
          if (data.type === 'audio') {
            setIsAgentSpeaking(true);
            
            // Play the audio chunk
            if (data.audio_event?.audio_base_64) {
              try {
                // Create audio blob from base64 and play using HTML5 audio
                const audioData = data.audio_event.audio_base_64;
                const byteCharacters = atob(audioData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                // Create blob with appropriate audio format
                const audioBlob = new Blob([byteArray], { type: 'audio/wav' });
                const audioUrl = URL.createObjectURL(audioBlob);
                
                // Create and play audio element
                const audio = new Audio(audioUrl);
                audio.play()
                  .then(() => {
                    // Clean up URL after playing
                    audio.onended = () => {
                      URL.revokeObjectURL(audioUrl);
                    };
                  })
                  .catch(error => {
                    URL.revokeObjectURL(audioUrl);
                  });

              } catch (error) {
              }
            }
          }
          
        } catch (error) {
        }
      };

      ws.onerror = (error) => {
        setIsConnected(false);
        setIsConnecting(false);
        isStartingRef.current = false;
        stopAudioCapture();
      };

      ws.onclose = (event) => {
        // Stop any currently playing TTS audio
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }
        
        setIsConnected(false);
        setIsConnecting(false);
        setIsAgentSpeaking(false);
        setIsProcessing(false);
        isStartingRef.current = false;
        stopAudioCapture();
      };

    } catch (error) {
      setIsConnected(false);
      setIsConnecting(false);
      setCanRetry(true);
      isStartingRef.current = false;
      stopAudioCapture();

      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        setTimeout(() => startVoiceConversation(), 2000);
      }
    }
  }, [user?.id, positId, isConnected, handleTranscriptReceived, startAudioCapture]);

  const endVoiceConversation = useCallback(() => {
    // Stop any currently playing TTS audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    // Clear transcript buffer and timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    setTranscriptBuffer([]);
    
    stopAudioCapture();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setIsAgentSpeaking(false);
    setIsProcessing(false);
    setConversationId(null);
    isStartingRef.current = false;
  }, [stopAudioCapture]);

  // Cleanup on unmount and browser navigation
  useEffect(() => {
    // Handle browser back button and page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      endVoiceConversation();
      // Don't show confirmation dialog for voice sessions
      event.preventDefault();
    };

    const handleUnload = () => {
      endVoiceConversation();
    };

    // Add event listeners for browser navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      // Remove event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);

      // Always end conversation on unmount
      endVoiceConversation();
    };
  }, [endVoiceConversation, isConnected, user]);

  return {
    isConnected,
    isConnecting,
    isProcessing,
    isAgentSpeaking,
    conversationId,
    canRetry,
    startVoiceConversation,
    endVoiceConversation
  };
}; 